import { useState } from "react";
import { Maximize2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PRODUCTS, POWDER_STEPS } from "@/data/site";

const Lightbox = ({ src, alt, open, onClose }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl max-h-[92vh] p-0 overflow-hidden rounded-2xl bg-[#06140E] border-emerald-900/60 [&>button]:hidden" data-testid="guide-lightbox">
      <DialogTitle className="sr-only">{alt}</DialogTitle>
      <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-900/60">
        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-200">Zoryven Herbal Hair Powder — Instruction Card</span>
        <button type="button" onClick={onClose} data-testid="guide-lightbox-close-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-rose-600 border border-slate-700 text-slate-100 text-xs font-bold uppercase tracking-wider transition-colors">
          <X className="w-4 h-4" /> Close
        </button>
      </div>
      <div className="overflow-y-auto max-h-[calc(92vh-3.5rem)]">
        <img src={src} alt={alt} className="w-full h-auto" data-testid="guide-lightbox-image" />
      </div>
    </DialogContent>
  </Dialog>
);

export const UsageGuide = ({ tone = "rose" }) => {
  const [open, setOpen] = useState(false);
  const powder = PRODUCTS.find((p) => p.name === "Hair Powder");
  const alt = "Zoryven Herbal Hair Powder usage instructions in Hindi and English";
  return (
    <div id="usage-guide" className={`rounded-3xl border overflow-hidden grid grid-cols-1 lg:grid-cols-12 ${tone === "emerald" ? "bg-[#06140E] border-emerald-900/50" : "bg-slate-950 border-slate-800"}`} data-testid="usage-guide">
      <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col justify-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-emerald-300">How to Use Our Products</span>
        <h3 className="font-condensed text-2xl sm:text-3xl uppercase text-white mt-2">Zoryven Herbal Hair Powder — Step-by-Step</h3>
        <p className="text-sm text-slate-400 mt-3">Our hair-fall and alopecia treatment works as a course. Follow the card carefully — instructions are given in Hindi and English.</p>
        <ul className="mt-5 space-y-2 text-sm text-slate-300">
          {POWDER_STEPS.map((step, i) => (
            <li key={step} className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
              {step}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs text-slate-500">Tap the card to enlarge it, or open the Hair Powder product for full details.</p>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Enlarge instruction card"
        data-testid="usage-guide-image-btn"
        className={`group relative lg:col-span-7 border-t lg:border-t-0 lg:border-l block text-left ${tone === "emerald" ? "bg-[#0B1E16] border-emerald-900/50" : "bg-slate-900 border-slate-800"}`}
      >
        <img src={powder.guideImg} alt={alt} loading="lazy" className="w-full h-full max-h-[720px] object-contain" data-testid="usage-guide-image" />
        <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 backdrop-blur text-[11px] font-bold uppercase tracking-wider text-white opacity-80 group-hover:opacity-100 transition-opacity">
          <Maximize2 className="w-3.5 h-3.5" /> Tap to enlarge
        </span>
      </button>
      <Lightbox src={powder.guideImg} alt={alt} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};
